import React, { useState, createContext, useContext, useEffect, Suspense, lazy } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { RouterProvider } from 'react-router-dom';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { SEOHead, seoConfigs } from './components/SEOHead';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useWebVitals } from './hooks/usePerformance';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { router } from './routes';
import { Toaster } from './components/ui/sonner';
import { CookieConsentBanner } from './components/CookieConsentBanner';

// Lazy load pages for better performance
const LandingPage = lazy(() => import('./components/pages/LandingPage').then(module => ({ default: module.LandingPage })));
const FeaturesPage = lazy(() => import('./components/pages/FeaturesPage').then(module => ({ default: module.FeaturesPage })));
const PricingPage = lazy(() => import('./components/pages/PricingPage').then(module => ({ default: module.PricingPage })));
const DocsPage = lazy(() => import('./components/pages/DocsPage').then(module => ({ default: module.DocsPage })));
const BlogPage = lazy(() => import('./components/pages/BlogPage').then(module => ({ default: module.BlogPage })));
const ContactPage = lazy(() => import('./components/pages/ContactPage').then(module => ({ default: module.ContactPage })));
const SignInPage = lazy(() => import('./components/pages/SignInPage').then(module => ({ default: module.SignInPage })));
const SignUpPage = lazy(() => import('./components/pages/SignUpPage').then(module => ({ default: module.SignUpPage })));

const ProductPage = lazy(() => import('./components/pages/PublicMarketingPages').then((m) => ({ default: m.ProductPage })));
const AboutPage = lazy(() => import('./components/pages/PublicMarketingPages').then((m) => ({ default: m.AboutPage })));
const CareersPage = lazy(() => import('./components/pages/PublicMarketingPages').then((m) => ({ default: m.CareersPage })));
const HelpCenterMarketingPage = lazy(() => import('./components/pages/PublicMarketingPages').then((m) => ({ default: m.HelpCenterMarketingPage })));
const StatusPage = lazy(() => import('./components/pages/PublicMarketingPages').then((m) => ({ default: m.StatusPage })));
const SecurityMarketingPage = lazy(() => import('./components/pages/PublicMarketingPages').then((m) => ({ default: m.SecurityMarketingPage })));
const ApiReferencePage = lazy(() => import('./components/pages/PublicMarketingPages').then((m) => ({ default: m.ApiReferencePage })));
const ApiDocsPage = lazy(() => import('./components/pages/PublicMarketingPages').then((m) => ({ default: m.ApiDocsPage })));
const SdksPage = lazy(() => import('./components/pages/PublicMarketingPages').then((m) => ({ default: m.SdksPage })));
const IntegrationsMarketingPage = lazy(() => import('./components/pages/PublicMarketingPages').then((m) => ({ default: m.IntegrationsMarketingPage })));
const WebhooksPage = lazy(() => import('./components/pages/PublicMarketingPages').then((m) => ({ default: m.WebhooksPage })));
const TrustCenterPage = lazy(() => import('./components/pages/PublicMarketingPages').then((m) => ({ default: m.TrustCenterPage })));
const TermsOfServicePage = lazy(() => import('./components/pages/PublicMarketingPages').then((m) => ({ default: m.TermsOfServicePage })));
const PrivacyPolicyPage = lazy(() => import('./components/pages/PublicMarketingPages').then((m) => ({ default: m.PrivacyPolicyPage })));
const CookiePolicyPage = lazy(() => import('./components/pages/PublicMarketingPages').then((m) => ({ default: m.CookiePolicyPage })));
const DpaPage = lazy(() => import('./components/pages/PublicMarketingPages').then((m) => ({ default: m.DpaPage })));
const EnterpriseSaaSAgreementPage = lazy(() => import('./components/pages/PublicMarketingPages').then((m) => ({ default: m.EnterpriseSaaSAgreementPage })));
const SlaAgreementPage = lazy(() => import('./components/pages/PublicMarketingPages').then((m) => ({ default: m.SlaAgreementPage })));
const BillingTermsPage = lazy(() => import('./components/pages/PublicMarketingPages').then((m) => ({ default: m.BillingTermsPage })));
const SecurityAddendumPage = lazy(() => import('./components/pages/PublicMarketingPages').then((m) => ({ default: m.SecurityAddendumPage })));

// Theme Context
const ThemeContext = createContext<{
  theme: 'light' | 'dark';
  toggleTheme: () => void;
}>({
  theme: 'dark',
  toggleTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

// Re-export useAuth for convenience
export { useAuth } from './contexts/AuthContext';

// Router Context
const RouterContext = createContext<{
  currentPage: string;
  navigate: (page: string) => void;
}>({
  currentPage: 'home',
  navigate: () => {},
});

export const useRouter = () => useContext(RouterContext);

const PUBLIC_PAGE_PATHS: Record<string, string> = {
  home: '/',
  product: '/product',
  features: '/features',
  pricing: '/pricing',
  docs: '/docs',
  blog: '/blog',
  contact: '/contact',
  signin: '/signin',
  signup: '/signup',
  about: '/about',
  careers: '/careers',
  'help-center': '/help-center',
  status: '/status',
  security: '/security',
  'api-reference': '/api-reference',
  'api-docs': '/api-docs',
  sdks: '/sdks',
  'integrations-dev': '/integrations-dev',
  webhooks: '/webhooks',
  'trust-center': '/trust-center',
  terms: '/terms',
  privacy: '/privacy',
  cookies: '/cookies',
  dpa: '/dpa',
  'enterprise-saas': '/enterprise-saas',
  sla: '/sla',
  'billing-terms': '/billing-terms',
  'security-addendum': '/security-addendum',
};

const DASHBOARD_PATH_PREFIXES = [
  '/overview',
  '/onboarding',
  '/monitoring',
  '/server-mgmt',
  '/ai',
  '/microservices',
  '/ticketing',
  '/incident-commander',
  '/incidents',
  '/team',
  '/roles-permissions',
  '/collaboration',
  '/achievements',
  '/system',
  '/payment',
  '/billing',
  '/subscription',
  '/invoices',
  '/support',
  '/help-center',
  '/contact-support',
  '/documentation',
  '/legal',
  '/time-machine',
];

const pathToPublicPage = (pathname: string): string => {
  const cleanPath = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
  const matched = Object.entries(PUBLIC_PAGE_PATHS).find(([, path]) => path === cleanPath);
  if (matched) return matched[0];
  const looksLikeDashboardRoute = DASHBOARD_PATH_PREFIXES.some((prefix) => cleanPath.startsWith(prefix));
  return looksLikeDashboardRoute ? 'signin' : 'home';
};

const publicPageToPath = (page: string): string => PUBLIC_PAGE_PATHS[page] ?? '/';

// Loading component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="relative">
      <div className="w-16 h-16 border-4 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin" />
      <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-blue-500 rounded-full animate-spin animation-delay-150" />
    </div>
  </div>
);

// Inner App component (wrapped by AuthProvider)
function InnerApp() {
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');
  const [currentPage, setCurrentPage] = useState(() => pathToPublicPage(window.location.pathname));
  const vitals = useWebVitals();
  const { isAuthenticated, user } = useAuth();

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const navigate = (page: string) => {
    const nextPage = PUBLIC_PAGE_PATHS[page] ? page : 'home';
    const nextPath = publicPageToPath(nextPage);
    if (window.location.pathname !== nextPath) {
      window.history.pushState({}, '', nextPath);
    }
    setCurrentPage(nextPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && Object.keys(vitals).length > 0) {
      console.group('🔥 Core Web Vitals');
      if (vitals.FCP) console.log(`First Contentful Paint: ${vitals.FCP.toFixed(2)}ms`);
      if (vitals.LCP) console.log(`Largest Contentful Paint: ${vitals.LCP.toFixed(2)}ms`);
      if (vitals.FID) console.log(`First Input Delay: ${vitals.FID.toFixed(2)}ms`);
      if (vitals.CLS) console.log(`Cumulative Layout Shift: ${vitals.CLS.toFixed(4)}`);
      console.groupEnd();
    }
  }, [vitals]);

  useEffect(() => {
    if (isAuthenticated) return;

    const syncPageFromUrl = () => {
      const pageFromUrl = pathToPublicPage(window.location.pathname);
      const canonicalPath = publicPageToPath(pageFromUrl);
      setCurrentPage(pageFromUrl);
      if (window.location.pathname !== canonicalPath) {
        window.history.replaceState({}, '', canonicalPath);
      }
    };

    syncPageFromUrl();
    window.addEventListener('popstate', syncPageFromUrl);
    return () => window.removeEventListener('popstate', syncPageFromUrl);
  }, [isAuthenticated]);

  if (isAuthenticated && user) {
    return (
      <ErrorBoundary>
        <ThemeContext.Provider value={{ theme, toggleTheme }}>
          <SEOHead
            title="CoreMonit Dashboard - AI-Powered Infrastructure Monitoring"
            description="Real-time monitoring dashboard with AI anomaly detection and auto-remediation"
            keywords={["monitoring dashboard", "AI infrastructure", "real-time analytics"]}
          />
          <div className="min-h-screen bg-background text-foreground transition-colors duration-500">
            <CookieConsentBanner />
            <RouterProvider router={router} />
            <Toaster />
          </div>
        </ThemeContext.Provider>
      </ErrorBoundary>
    );
  }

  const renderPage = () => {
    const pageComponents: Record<string, React.LazyExoticComponent<React.ComponentType<unknown>>> = {
      home: LandingPage,
      product: ProductPage,
      features: FeaturesPage,
      pricing: PricingPage,
      docs: DocsPage,
      blog: BlogPage,
      contact: ContactPage,
      signin: SignInPage,
      signup: SignUpPage,
      about: AboutPage,
      careers: CareersPage,
      'help-center': HelpCenterMarketingPage,
      status: StatusPage,
      security: SecurityMarketingPage,
      'api-reference': ApiReferencePage,
      'api-docs': ApiDocsPage,
      sdks: SdksPage,
      'integrations-dev': IntegrationsMarketingPage,
      webhooks: WebhooksPage,
      'trust-center': TrustCenterPage,
      terms: TermsOfServicePage,
      privacy: PrivacyPolicyPage,
      cookies: CookiePolicyPage,
      dpa: DpaPage,
      'enterprise-saas': EnterpriseSaaSAgreementPage,
      sla: SlaAgreementPage,
      'billing-terms': BillingTermsPage,
      'security-addendum': SecurityAddendumPage,
    };

    const PageComponent = pageComponents[currentPage] || LandingPage;
    return <PageComponent />;
  };

  const currentSEOConfig = seoConfigs[currentPage as keyof typeof seoConfigs] || seoConfigs.home;

  return (
    <ErrorBoundary>
      <ThemeContext.Provider value={{ theme, toggleTheme }}>
        <RouterContext.Provider value={{ currentPage, navigate }}>
          <SEOHead {...currentSEOConfig} />
          <div className="min-h-screen bg-background text-foreground transition-colors duration-500">
            <div className="fixed inset-0 -z-10">
              <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-background" />
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-cyan-500/5" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(120,119,198,0.1),transparent_50%)] dark:bg-[radial-gradient(circle_at_50%_50%,rgba(120,119,198,0.05),transparent_50%)]" />
            </div>
            <CookieConsentBanner />
            <Header />
            <main className="relative">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentPage}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  <Suspense fallback={<LoadingSpinner />}>
                    {renderPage()}
                  </Suspense>
                </motion.div>
              </AnimatePresence>
            </main>
            <Footer />
            <Toaster />
          </div>
        </RouterContext.Provider>
      </ThemeContext.Provider>
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <InnerApp />
    </AuthProvider>
  );
}
